import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  message,
  Spin,
  Alert,
  Typography,
  Divider,
  Row,
  Col,
  Card
} from 'antd';
import {
  FileTextOutlined,
  MedicineBoxOutlined,
  ExperimentOutlined,
  RadarChartOutlined,
  TeamOutlined,
  HeartOutlined,
  SaveOutlined,
  CloseOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { PLAN_CATEGORIES, validatePlanData } from '../utils/problemPlanUtils';
import '../styles/ProblemPlan.css';

const { TextArea } = Input;
const { Title, Text } = Typography;

const ProblemPlanModal = ({
  visible,
  onCancel,
  problem,
  onPlanUpdated
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t, i18n } = useTranslation();

  // Plan categories with their respective icons
  const planCategoriesWithIcons = PLAN_CATEGORIES.map(category => ({
    ...category,
    icon: category.key === 'general' ? <FileTextOutlined /> :
          category.key === 'medications' ? <MedicineBoxOutlined /> :
          category.key === 'labs' ? <ExperimentOutlined /> :
          category.key === 'diagnostics' ? <RadarChartOutlined /> :
          category.key === 'referrals' ? <TeamOutlined /> :
          category.key === 'lifestyle' ? <HeartOutlined /> :
          <FileTextOutlined />
  }));

  // Initialize form when problem changes
  useEffect(() => {
    if (visible && problem) {
      const planData = {};
      PLAN_CATEGORIES.forEach(category => {
        planData[category.key] = problem.plan?.[category.key] || '';
      });
      form.setFieldsValue(planData);
      setError(null);
    }
  }, [visible, problem, form]);

  // Handle form submission
  const handleFinish = async (values) => {
    if (!problem?._id) {
      message.error('Problem ID is missing');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare plan data
      const planData = {
        plan: {}
      };

      PLAN_CATEGORIES.forEach(category => {
        planData.plan[category.key] = values[category.key]?.trim() || '';
      });

      // Validate plan data
      const validation = validatePlanData(planData.plan);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        message.error(validation.errors.join(', '));
        return;
      }

      // Update problem with plan data
      const response = await axios.patch(`/problems/${problem._id}`, planData);
      
      message.success('Plan updated successfully!');
      
      // Notify parent component of the update
      if (onPlanUpdated) {
        onPlanUpdated(response.data.data.problem);
      }
      
      onCancel();
    } catch (err) {
      console.error('Error updating problem plan:', err);
      
      let errorMessage = 'Failed to update plan.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 404) {
        errorMessage = 'Problem not found.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to update this problem.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid plan data. Please check your inputs.';
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  // Get current language
  const isVietnamese = i18n.language === 'vi';

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          {isVietnamese ? `Kế hoạch cho: ${problem?.problemName || 'Vấn đề'}` : `Plan for: ${problem?.problemName || 'Problem'}`}
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={600}
      className="problem-plan-modal"
      data-language={isVietnamese ? 'vi' : 'en'}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          <CloseOutlined />
          {isVietnamese ? 'Hủy' : 'Cancel'}
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
          className="plan-button"
        >
          <SaveOutlined />
          {isVietnamese ? 'Lưu kế hoạch' : 'Save Plan'}
        </Button>
      ]}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {problem && (
          <div className="problem-info-display">
            <div className="problem-name">
              {problem.problemName}
            </div>
            {problem.icdCode && (
              <span className="problem-code">
                {problem.icdCode}
              </span>
            )}
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {planCategoriesWithIcons.map((category, index) => (
              <Card
                key={category.key}
                size="small"
                title={
                  <Space size="small">
                    <span className={`plan-category-icon ${category.key}`}>
                      {category.icon}
                    </span>
                    <Text strong style={{ fontSize: '14px' }}>
                      {isVietnamese ? category.label_vn : category.label}
                    </Text>
                  </Space>
                }
                style={{
                  marginBottom: 0,
                  border: '1px solid #f0f0f0',
                  borderRadius: '6px'
                }}
                bodyStyle={{ padding: '12px 16px' }}
                headStyle={{
                  padding: '8px 16px',
                  minHeight: 'auto',
                  borderBottom: '1px solid #f0f0f0'
                }}
              >
                <Form.Item
                  name={category.key}
                  style={{ marginBottom: 0 }}
                  rules={[
                    {
                      max: 2000,
                      message: `${isVietnamese ? category.label_vn : category.label} plan cannot exceed 2000 characters`
                    }
                  ]}
                >
                  <TextArea
                    rows={3}
                    placeholder={isVietnamese ? category.placeholder_vn : category.placeholder}
                    maxLength={2000}
                    showCount
                    style={{
                      resize: 'vertical',
                      fontSize: '14px'
                    }}
                  />
                </Form.Item>
              </Card>
            ))}
          </Space>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ProblemPlanModal;
